import { ColumnNames } from '$lib/models/client/types';
import { type BaseListResponse, DateFilters, SortBy } from '$lib/models/types';
import type { ClearSsoConfigRequest, ClientSsoOidcConfig, ClientSsoSamlConfig, ClientSsoSummary, SSOType } from '$lib/types';
import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { extractErrorMessage, handleApiError } from '$lib/utils/error/errorUtils';
import { showToast } from '$lib/helpers/toastrHelper';
import type { State } from '@vincjo/datatables/local';
import { writable } from 'svelte/store';

function createClientSsoConfigStore() {
  const { subscribe, set, update } = writable<ClientSsoSummary[]>([]);
  const baseUrl: string = '/api/sso';

  return {
    subscribe,
    fetch: async (state: State): Promise<BaseListResponse> => {
      const { pageNumber, rowsPerPage, sort, filters } = state;

      let url = `${baseUrl}/summary`;

      const sortBy: string =
        sort?.direction?.toString() != undefined ? (sort?.direction?.toString().toLowerCase() == 'asc' ? SortBy.Ascending : SortBy.Descending) : SortBy.Descending;

      url += `?pageNumber=${pageNumber}&pageSize=${rowsPerPage}&sortBy=${sortBy}&orderBy=${sort?.orderBy}`;
      if (filters) {
        url += filters
          .map(({ filterBy, value }) => {
            if (filterBy == ColumnNames.CreateDate) {
              const date = new Date();

              if (value === DateFilters.LastSevenDays) {
                date.setDate(date.getDate() - 7);
                return `&${filterBy}=${date.toISOString()}`;
              }
              if (value === DateFilters.LastFourteenDays) {
                date.setDate(date.getDate() - 14);
                return `&${filterBy}=${date.toISOString()}`;
              }
              if (value === DateFilters.LastMonth) {
                date.setMonth(date.getMonth() - 1);
                return `&${filterBy}=${date.toISOString()}`;
              }

              if (value == DateFilters.LastQuarter) {
                date.setMonth(date.getMonth() - 3);
                return `&${filterBy}=${date.toISOString()}`;
              }

              if (value == DateFilters.LastTwelveMonths) {
                date.setFullYear(date.getFullYear() - 1);
                return `&${filterBy}=${date.toISOString()}`;
              }

              return `&${filterBy}=${date.toISOString()}`;
            }

            return `&${filterBy}=${value}`;
          })
          .join('');
      }

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch SSO configs (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return { total: 0, data: [] };
      }

      try {
        return await response.json();
      } catch (error) {
        showToast({
          title: 'Error',
          message: 'Failed to parse SSO config data',
          type: 'error'
        });
        return { total: 0, data: [] };
      }
    },
    getConfigDetails: async (clientId: number, ssoType: SSOType): Promise<ClientSsoSamlConfig | ClientSsoOidcConfig | ClientSsoSummary | null> => {
      try {
        const response = await fetchWithAuth(`${baseUrl}/detail/${clientId}/${ssoType}`);
        if (!response.ok) {
          const errorMessage = await extractErrorMessage(response, `Failed to fetch SSO config details (HTTP ${response.status})`);
          showToast({
            title: 'Error',
            message: errorMessage,
            type: 'error'
          });
          throw new Error(errorMessage);
        }
        return await response.json();
      } catch (error) {
        const errorMessage = handleApiError(error as Error, 'Error fetching SSO config details');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        console.error('Error fetching SSO config details:', error);
        return null;
      }
    },
    updateSamlConfig: async (updatedConfig: ClientSsoSamlConfig) => {
      if (!updatedConfig.ssoProviderId && updatedConfig.subdomain) updatedConfig.ssoProviderId = 'saml.' + updatedConfig.subdomain;

      try {
        const response = await fetchWithAuth(`${baseUrl}/saml-config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedConfig),
        });
        if (!response.ok) {
          const errorBody = await response.json();
          throw new Error(`Failed to update SSO config: ${errorBody.detail || errorBody}`);
        }

        const returnedConfig: ClientSsoSamlConfig = await response.json();
        update((configs) =>
          configs.map((config) =>
            config.clientId === returnedConfig.clientId
              ? {
                  clientId: returnedConfig.clientId,
                  clientName: returnedConfig.clientName,
                  subdomain: returnedConfig.subdomain,
                  isAutoAccountCreationEnabled: returnedConfig.isAutoAccountCreationEnabled,
                  ssoProviderId: returnedConfig.ssoProviderId,
                }
              : config
          )
        );
        return returnedConfig;
      } catch (error) {
        const errorMessage = handleApiError(error as Error, 'Error updating SSO config');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        console.error('Error updating SSO config:', error);
        throw error;
      }
    },
    updateOidcConfig: async (updatedConfig: ClientSsoOidcConfig) => {
      if (!updatedConfig.ssoProviderId && updatedConfig.subdomain) updatedConfig.ssoProviderId = 'oidc.' + updatedConfig.subdomain;

      try {
        const response = await fetchWithAuth(`${baseUrl}/oidc-config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedConfig),
        });
        if (!response.ok) {
          const errorBody = await response.json();
          throw new Error(`Failed to update SSO config: ${errorBody.detail || errorBody}`);
        }
        const returnedConfig: ClientSsoOidcConfig = await response.json();
        update((configs) =>
          configs.map((config) =>
            config.clientId === returnedConfig.clientId
              ? {
                  clientId: returnedConfig.clientId,
                  clientName: returnedConfig.clientName,
                  subdomain: returnedConfig.subdomain,
                  isAutoAccountCreationEnabled: returnedConfig.isAutoAccountCreationEnabled,
                  ssoProviderId: returnedConfig.ssoProviderId,
                }
              : config
          )
        );
        return returnedConfig;
      } catch (error) {
        const errorMessage = handleApiError(error as Error, 'Error updating OIDC config');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        console.error('Error updating OIDC config:', error);
        throw error;
      }
    },
    clearSsoConfig: async (summary: ClientSsoSummary) => {
      const clearConfig: ClearSsoConfigRequest = {
        clientId: summary.clientId,
        subdomain: summary.subdomain,
        isAutoAccountCreationEnabled: summary.isAutoAccountCreationEnabled,
      };

      try {
        const response = await fetchWithAuth(`${baseUrl}/clear-config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clearConfig),
        });
        if (!response.ok) {
          const errorBody = await response.json();
          throw new Error(`Failed to clear SSO config: ${errorBody.detail || errorBody}`);
        }

        summary.ssoProviderId = '';
        update((configs) =>
          configs.map((config) =>
            config.clientId === summary.clientId
              ? {
                  ...summary,
                }
              : config
          )
        );
      } catch (error) {
        const errorMessage = handleApiError(error as Error, 'Error clearing SSO config');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        console.error('Error clearing SSO config:', error);
        throw error;
      }
    },
  };
}

export const clientSsoConfigStore = createClientSsoConfigStore();
